// US-076 — Green confirmation strip shown after the user reports an outcome.
//
// Extracted verbatim from `ThreadPanel` as part of the audit-cleanup
// structural split (carrying its `outcomeLabel` helper along). The parent
// keeps the `conversation.pendingAction && conversation.outcomeStatus` guard;
// this component only renders once an outcome exists.

import type { OutcomeStatus } from '@barel/core';
import { useT } from '@barel/i18n';
import { Banner } from '@barel/ui';

export interface OutcomeConfirmationProps {
  outcomeStatus: OutcomeStatus;
  /** Epoch ms of the next queued relance, when one exists. */
  relanceScheduledAt?: number | null;
}

export function OutcomeConfirmation({
  outcomeStatus,
  relanceScheduledAt,
}: OutcomeConfirmationProps) {
  const t = useT('inbox');
  const followupMissing = outcomeStatus === 'followup' && !relanceScheduledAt;

  return (
    <div
      style={{ padding: '0 var(--space-4) var(--space-3)' }}
      data-ls-region="outcome-confirmation"
    >
      <Banner
        tone={followupMissing ? 'warning' : 'success'}
        title={t('confirmation.action_saved', {
          label: outcomeLabel(t, outcomeStatus),
        })}
      >
        {outcomeStatus === 'followup' && relanceScheduledAt
          ? t('confirmation.relance_scheduled', {
              date: new Date(relanceScheduledAt).toLocaleString(),
            })
          : followupMissing
            ? t('confirmation.relance_not_scheduled')
            : null}
      </Banner>
    </div>
  );
}

function outcomeLabel(
  t: (key: string, params?: Record<string, unknown>) => string,
  status: 'followup' | 'archive' | 'client' | 'won' | 'lost' | 'unknown',
): string {
  switch (status) {
    case 'followup':
      return t('thread.pending.outcomeFollowupLabel');
    case 'archive':
      return t('thread.pending.outcomeArchiveLabel');
    case 'client':
      return t('outcome.client');
    case 'won':
      return t('thread.pending.outcomeWonLabel');
    case 'lost':
      return t('thread.pending.outcomeLostLabel');
    case 'unknown':
      return t('thread.pending.outcomeUnknownLabel');
  }
}
